import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";

import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import Toolbar from "@mui/material/Toolbar";
import ButtonGroup from "@mui/material/ButtonGroup";
import Fab from "@mui/material/Fab";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";

import { KMKDrawer } from "../../../../../Common";
import Details from "./Detail";
import Lov from "./ListofValue";
import DQMTab from "./DQM";
import DSContext from "../DSContext";

const drawerWidth = 420;

const useStyles = makeStyles()((theme) => ({
  heading: {
    color: "#DF672A",
    fontWeight: "bold",
    fontSize: "1rem",
    wordBreak: "break-all",
    "& span": {
      fontWeight: "normal",
    },
  },
  closeIcon: {
    padding: 0,
  },
  container: {
    height: "100%",
    padding: "25px 30px",
    "& .errorMessage .MuiPaper-root": {
      marginTop: "20px",
      alignItems: "flex-start",
      border: "none",
    },
    "& .errorMessage .MuiAlert-message": {
      color: "#C72C2C",
    },
  },
  fabButtonActive: {
    background: "linear-gradient(95deg, #11ab97 0%, #27354f 100%);",
    color: "white",
    boxShadow: "none",
    minWidth: "120px",
    "&:hover": {
      color: "white",
    },
    textTransform: "none",
  },
  fabButton: {
    background: "white",
    color: "black",
    boxShadow: "none",
    minWidth: "120px",
    "&:hover": {
      color: "black",
    },
    textTransform: "none",
    "&.Mui-disabled": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
}));

const AddField = ({
  setOpenAddField,
  addField,
  currentField,
  dataTableRef,
  openTab,
  setCurrentField,
  newDatasource,
  IsDataVaultEnabled,
  containsIsDeleted,
  fetchDSRequiredInfo,
}) => {
  const { classes } = useStyles();
  const dataSourceData = useSelector((state) => state.dataSourceData);
  const { status: dsStatus } = dataSourceData;

  const { fields } = useContext(DSContext);

  const [openedTab, setOpenedTab] = useState(openTab);
  const details = openedTab === "details";
  const lov = openedTab === "LoV";
  const DQM = openedTab === "DQM";

  const dqmRef = useRef(null);
  const detailRef = useRef(null);
  const lovRef = useRef(null);

  const currentFieldObj = useMemo(() => fields.find((field) => field.name === currentField), [currentField, fields]);

  const [formErrorMessage, setFormErrorMessage] = useState("");
  const isDeleted = false;

  const onClose = () => setOpenAddField(false);

  const handleTabclick = (clickedTab) => {
    setFormErrorMessage("");
    if (details && detailRef.current.enableDetailsSave()) setFormErrorMessage("Please enter required(*) fields.");
    else if (DQM && dqmRef.current.enableDQMSave()) setFormErrorMessage("Please enter required(*) fields.");
    else {
      if (details) detailRef.current.handleSaveDetails("auto");
      else if (DQM) dqmRef.current.handleSaveDetails("auto");
      else lovRef.current.handleSaveDetails("auto");
      setOpenedTab(clickedTab);
    }
  };

  useEffect(() => {
    if (dsStatus === "notFetched") fetchDSRequiredInfo();
  }, [fetchDSRequiredInfo, dsStatus]);

  if (dsStatus === "notFetched") return null;

  return (
    <KMKDrawer
      anchor="right"
      open={true}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: { xs: "100%", sm: "450px" },
          boxSizing: "border-box",
        },
      }}
    >
      <Toolbar />
      <Box className={classes.container} role="presentation">
        <Box sx={{ padding: "0px 5px 20px 20px", display: "flex" }}>
          <Box flexGrow={1}>
            <Typography className={classes.heading} variant="h6" gutterBottom component="div">
              Attribute : <span>{currentField || "New Field"}</span>
            </Typography>
          </Box>
          <IconButton className={classes.closeIcon} onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            marginTop: "10px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <ButtonGroup className="tabtext" sx={{ borderRadius: "200px", width: "360px" }} variant="contained">
            <Fab
              variant="extended"
              size="small"
              className={details ? classes.fabButtonActive : classes.fabButton}
              onClick={(e) => handleTabclick("details")}
            >
              Details
            </Fab>
            <Fab
              variant="extended"
              size="small"
              className={DQM ? classes.fabButtonActive : classes.fabButton}
              onClick={(e) => handleTabclick("DQM")}
              disabled={!currentFieldObj?.name || isDeleted}
            >
              DQM
            </Fab>
            <Fab
              variant="extended"
              size="small"
              className={lov ? classes.fabButtonActive : classes.fabButton}
              onClick={(e) => handleTabclick("LoV")}
              disabled={!currentFieldObj?.name || isDeleted}
            >
              LoV
            </Fab>
          </ButtonGroup>
        </Box>

        <>
          {/* {formErrorMessage && ((details && enableDetailsSave()) || (DQM && enableDQMSave())) ? ( */}
          {formErrorMessage ? (
            <Stack className="errorMessage" sx={{ width: "100%", marginBottom: "20px" }} spacing={2}>
              <Alert severity="error">{formErrorMessage}</Alert>
            </Stack>
          ) : (
            ""
          )}
          {details && (
            <Details
              ref={detailRef}
              addField={addField}
              dataTableRef={dataTableRef}
              setCurrentField={setCurrentField}
              onClose={onClose}
              currentField={currentField}
              newDatasource={newDatasource}
              IsDataVaultEnabled={IsDataVaultEnabled}
              containsIsDeleted={containsIsDeleted}
            />
          )}
          {DQM && <DQMTab ref={dqmRef} addField={addField} onClose={onClose} currentField={currentField} />}

          {lov && (
            <Lov ref={lovRef} addField={addField} onClose={onClose} currentField={currentField} fields={fields} />
          )}
        </>
      </Box>
    </KMKDrawer>
  );
};

export default AddField;
